import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { 
    Inbox, Trash2, ChevronLeft, 
    Mail, MailOpen, Send, Calendar, Reply
} from "lucide-react";
import { useSearchParams } from "react-router-dom";

// ==========================================
// STYLED COMPONENTS (WSPÓŁDZIELONE)
// ==========================================

const ManagerContainer = styled.div`
  width: 100%;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;

  h2 {
    margin: 0;
    font-size: 1.8rem;
    color: #111;
    font-weight: 800;
  }
`;

const PrimaryButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #111;
  color: #fff;
  padding: 10px 18px;
  border: none;
  border-radius: 8px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s;

  &:hover { background: #333; transform: translateY(-2px); }
  &:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }
`;

const SecondaryButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fff;
  color: #111;
  padding: 10px 18px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;

  &:hover { background: #f4f6f8; }
`;

const ActionBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 8px 12px;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  transition: 0.2s;

  &.read { background: #f0f4ff; color: #234ffc; }
  &.read:hover { background: #dce5ff; }

  &.delete { background: #fff0f0; color: #dc3545; }
  &.delete:hover { background: #ffe0e0; }
`;

// --- KARTY I LISTY ---

const MessageList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const MessageCard = styled.div`
  background: #fff;
  border-radius: 12px;
  border: 1px solid #eee;
  border-left: 4px solid ${props => props.isRead ? 'transparent' : '#234ffc'};
  box-shadow: 0 4px 15px rgba(0,0,0,0.02);
  padding: 20px 25px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0,0,0,0.06);
  }

  .content {
    display: flex;
    flex-direction: column;
    gap: 8px;

    .header {
      display: flex;
      align-items: center;
      gap: 10px;

      h3 { 
        margin: 0; 
        font-size: 1.15rem; 
        color: #111; 
        font-weight: ${props => props.isRead ? '600' : '800'}; 
      }
      
      span.date { 
        font-size: 0.85rem; 
        color: #999; 
        display: flex; 
        align-items: center; 
        gap: 4px;
      }
    }

    p { 
      margin: 0; 
      color: #666; 
      font-size: 0.95rem; 
      display: -webkit-box;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
    
    span.email { font-size: 0.85rem; color: #555; font-weight: 700; }
  }

  .actions { 
    display: flex; 
    gap: 10px; 
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
    
    .actions { width: 100%; justify-content: flex-end; }
  }
`;

// --- WIDOK CZYTANIA I ODPOWIEDZI ---

const ReadContainer = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 30px;
  border: 1px solid #eee;
  box-shadow: 0 4px 15px rgba(0,0,0,0.03);
  margin-bottom: 30px;
`;

const MessageMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid #eee;

  h3 { font-size: 1.5rem; margin: 0; color: #111; font-weight: 800; }
  .sender { color: #555; font-size: 1rem; strong { color: #111; } }
  .date { color: #999; font-size: 0.9rem; display: flex; align-items: center; gap: 5px; }
`;

const MessageBody = styled.div`
  font-size: 1.05rem;
  line-height: 1.7;
  color: #333;
  background: #f9f9f9;
  padding: 25px;
  border-radius: 8px;
  border: 1px solid #eee;
  white-space: pre-wrap;
  margin-bottom: 40px;
`;

const ReplyForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 15px;

  label {
    font-size: 1.1rem;
    font-weight: 800;
    color: #111;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  textarea {
    width: 100%;
    min-height: 150px;
    padding: 15px;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 1rem;
    font-family: inherit;
    resize: vertical;
    transition: border-color 0.3s;
    box-sizing: border-box;

    &:focus { outline: none; border-color: #111; }
  }
`;

// ==========================================
// GŁÓWNY KOMPONENT
// ==========================================

export default function AdminInbox() {
    const [messages, setMessages] = useState([]);
    
    // URL Params
    const [searchParams, setSearchParams] = useSearchParams();
    const viewState = searchParams.get("action") || "list";
    const readingId = searchParams.get("id") || null;

    const [loading, setLoading] = useState(false);
    const [replyText, setReplyText] = useState("");
    const [currentMsg, setCurrentMsg] = useState(null);

    const adresAPI = "https://api.pth-hokej.pl";

    const fetchMessages = async () => {
        try {
            const res = await fetch(`${adresAPI}/api/messages`);
            const data = await res.json();
            setMessages(data);
        } catch (err) {
            console.error("Błąd pobierania wiadomości:", err);
        }
    };

    useEffect(() => { 
        fetchMessages(); 
    }, []);

    // Logika ładowania konkretnej wiadomości
    useEffect(() => {
        if (viewState === "read" && readingId && messages.length > 0) {
            const msg = messages.find(m => m._id === readingId);
            if (msg) {
                setCurrentMsg(msg);
                
                // Jeśli otwieramy nieprzeczytaną wiadomość, odznaczamy ją jako przeczytaną
                if (!msg.read) {
                    markAsRead(msg._id);
                }
            }
        } else {
            setCurrentMsg(null);
            setReplyText("");
        }
    }, [viewState, readingId, messages]);

    const markAsRead = async (id) => {
        try {
            await fetch(`${adresAPI}/api/messages/${id}/read`, { method: "PATCH" });
            fetchMessages();
        } catch (err) {
            console.error(err);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Na pewno chcesz trwale usunąć tę wiadomość?")) return;
        try {
            await fetch(`${adresAPI}/api/messages/${id}`, { method: "DELETE" });
            fetchMessages();
            // Jeśli usuwamy podczas czytania, wracamy do listy
            if (viewState === "read") setSearchParams({}); 
        } catch (err) {
            console.error(err);
        }
    };

    const handleReply = async (e) => {
        e.preventDefault();
        if (!replyText.trim()) return;
        
        setLoading(true);
        try {
            await fetch(`${adresAPI}/api/messages/${currentMsg._id}/reply`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ replyText }),
            });
            alert("Odpowiedź została wysłana na adres email nadawcy!");
            setReplyText("");
            setSearchParams({}); // Powrót do listy
        } catch (err) {
            console.error("Błąd wysyłania odpowiedzi:", err);
            alert("Błąd podczas wysyłania odpowiedzi.");
        } finally {
            setLoading(false);
        }
    };

    const handleReadClick = (id) => {
        setSearchParams({ action: "read", id: id });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    return (
        <ManagerContainer>
            
            {/* WIDOK LISTY WIADOMOŚCI */}
            {viewState === "list" && (
                <>
                    <HeaderRow>
                        <h2>Skrzynka Odbiorcza</h2>
                        {/* Można tu dodać np. licznik nieprzeczytanych */}
                    </HeaderRow>

                    {messages.length === 0 ? (
                        <p style={{ color: "#666" }}>Skrzynka jest pusta. Brak nowych wiadomości.</p>
                    ) : (
                        <MessageList>
                            {messages.map((msg) => (
                                <MessageCard key={msg._id} isRead={msg.read}>
                                    <div className="content">
                                        <div className="header">
                                            {msg.read ? <MailOpen size={16} color="#999" /> : <Mail size={16} color="#234ffc" />}
                                            <h3>{msg.title}</h3>
                                            <span className="date">
                                                <Calendar size={12}/> 
                                                {new Date(msg.createdAt).toLocaleDateString('pl-PL')}
                                            </span>
                                        </div>
                                        <span className="email">{msg.email}</span>
                                        <p>{msg.content}</p>
                                    </div>
                                    <div className="actions">
                                        <ActionBtn type="button" className="read" onClick={() => handleReadClick(msg._id)}>
                                            <Reply size={16} /> Odpowiedz
                                        </ActionBtn>
                                        <ActionBtn type="button" className="delete" onClick={() => handleDelete(msg._id)}>
                                            <Trash2 size={16} />
                                        </ActionBtn>
                                    </div>
                                </MessageCard>
                            ))}
                        </MessageList>
                    )}
                </>
            )}

            {/* WIDOK CZYTANIA I ODPOWIEDZI */}
            {viewState === "read" && currentMsg && (
                <>
                    <HeaderRow>
                        <SecondaryButton onClick={() => setSearchParams({})}>
                            <ChevronLeft size={20} /> Wróć do skrzynki
                        </SecondaryButton>
                        <PrimaryButton style={{ background: '#dc3545' }} onClick={() => handleDelete(currentMsg._id)}>
                            <Trash2 size={18} /> Usuń Wiadomość
                        </PrimaryButton>
                    </HeaderRow>

                    <ReadContainer>
                        <MessageMeta>
                            <h3>{currentMsg.title}</h3>
                            <div className="sender">Od: <strong>{currentMsg.email}</strong></div>
                            <div className="date">
                                <Calendar size={14}/> 
                                Otrzymano: {new Date(currentMsg.createdAt).toLocaleString('pl-PL')}
                            </div>
                        </MessageMeta>

                        <MessageBody>
                            {currentMsg.content}
                        </MessageBody>

                 

                    </ReadContainer>
                </>
            )}
        </ManagerContainer>
    );
}